import { minBy, uniqBy } from 'es-toolkit';

import { Lumber } from '@paragon/contract-library';
import { assertExhaustiveSwitch } from '@paragon/utilities';

import { buildRankingsIncludingComboGrades, getGradeRank } from './lumberGradeRankings';
import { buildRankingsIncludingComboSpecies, getSpeciesRank } from './lumberSpeciesRankings';

// Lumber properties in order of importance: thickness > size > species > grade > treatment
// "Proposed thickness" is a special level that is considered most important.
export enum LumberPropertyLevel {
  Treatment = 0,
  Grade = 1,
  Species = 2,
  Width = 3,
  Thickness = 4,
  ProposedThickness = 5,
}

const lowestPropertyLevel = LumberPropertyLevel.Treatment;
const highestPropertyLevel = LumberPropertyLevel.ProposedThickness;

type LumberMatcher = (lumber1: Partial<Lumber>, lumber2: Partial<Lumber>) => boolean;
type LumberFilterByLevel = (
  allLumbers: readonly Lumber[],
  proposedLumber: Lumber,
  proposedThickness: number | undefined,
  level: LumberPropertyLevel,
) => readonly Lumber[];
type LumberFinder = (allLumbers: readonly Lumber[], proposedLumber: Lumber, thickness?: number) => Lumber;

export type HasNominalDimensions = { nominalThickness: string; nominalWidth: string };
export function hasNominalDimensions(lumber: Partial<Lumber>): lumber is HasNominalDimensions {
  return !!(lumber.nominalThickness && lumber.nominalWidth);
}

export const getNominalDimensionString = (lumber: HasNominalDimensions) =>
  `${lumber.nominalThickness}x${lumber.nominalWidth}`;

export const nominalSizeMatches: LumberMatcher = (lumber1, lumber2) =>
  lumber1.nominalThickness === lumber2.nominalThickness && lumber1.nominalWidth === lumber2.nominalWidth;

export function speciesMatches(lumber1: Partial<Lumber>, lumber2: Partial<Lumber>) {
  if (lumber1.species !== lumber2.species) return false;
  if (lumber1.species !== 'Combo') return true;
  const lumber1TangibleSpecies = lumber1.tangibleSpecies;
  const lumber2TangibleSpecies = lumber2.tangibleSpecies;
  if (!lumber1TangibleSpecies || !lumber2TangibleSpecies)
    throw new Error('Expected Combo lumber to have tangible species.');
  return (
    lumber1TangibleSpecies.length === lumber2TangibleSpecies.length &&
    lumber1TangibleSpecies.every((species) => lumber2TangibleSpecies.includes(species))
  );
}

export function gradeMatches(lumber1: Partial<Lumber>, lumber2: Partial<Lumber>) {
  if (lumber1.grade !== lumber2.grade) return false;
  if (lumber1.grade !== 'Combo') return true;
  const lumber1TangibleGrades = lumber1.tangibleGrades;
  const lumber2TangibleGrades = lumber2.tangibleGrades;
  if (!lumber1TangibleGrades || !lumber2TangibleGrades)
    throw new Error('Expected Combo lumber to have tangible grades.');
  return (
    lumber1TangibleGrades.length === lumber2TangibleGrades.length &&
    lumber1TangibleGrades.every((grade) => lumber2TangibleGrades.includes(grade))
  );
}

const treatmentTypeMatches: LumberMatcher = (lumberOne, lumberTwo) =>
  lumberOne.treatmentType === lumberTwo.treatmentType;

const getLumbersMatchingProposalAtLevel: LumberFilterByLevel = (lumbers, proposedLumber, thickness, level) => {
  switch (level) {
    case LumberPropertyLevel.ProposedThickness:
      return thickness == null
        ? lumbers
        : lumbers.filter((lumber) => lumberCanBeUsedForComponentThickness(lumber, thickness));
    case LumberPropertyLevel.Thickness:
      // Comparing nominal thickness helps us distinguish sawn and structural composite lumbers.
      return lumbers.filter((lumber) => lumber.nominalThickness === proposedLumber.nominalThickness);
    case LumberPropertyLevel.Width:
      // Comparing nominal width helps us distinguish sawn and structural composite lumbers.
      return lumbers.filter((lumber) => lumber.nominalWidth === proposedLumber.nominalWidth);
    case LumberPropertyLevel.Species:
      return lumbers.filter((lumber) => speciesMatches(lumber, proposedLumber));
    case LumberPropertyLevel.Grade:
      return lumbers.filter((lumber) => gradeMatches(lumber, proposedLumber));
    case LumberPropertyLevel.Treatment:
      return lumbers.filter((lumber) => treatmentTypeMatches(lumber, proposedLumber));
    default:
      assertExhaustiveSwitch(level, 'Unrecognized property level.');
  }
};

/**
 * A ranking holds only the combos it was built from, so the proposal has to be among them to have a rank of its own.
 * A set that does not stock the proposed combo is the case worth ranking, and leaving it out would leave nothing to
 * compare the stock against. Mirrored serverside where StockLumberBasedUpgrader builds its rankings.
 */
const comboSpeciesToRank = (validLumbers: readonly Lumber[], proposedLumber: Lumber) =>
  uniqBy(
    [...validLumbers, proposedLumber]
      .filter((lumber) => lumber.species === 'Combo')
      .map((lumber) => lumber.tangibleSpecies),
    (tangibleSpecies) => tangibleSpecies.toSorted().join(),
  );

/** The grade counterpart of comboSpeciesToRank. */
const comboGradesToRank = (validLumbers: readonly Lumber[], proposedLumber: Lumber) =>
  uniqBy(
    [...validLumbers, proposedLumber]
      .filter((lumber) => lumber.grade === 'Combo')
      .map((lumber) => lumber.tangibleGrades),
    (tangibleGrades) => tangibleGrades.toSorted().join(),
  );

export const getClosestLumberToProposedLumber: LumberFinder = (validLumbers, proposedLumber, thickness) =>
  getClosestLumberToProposedLumber_Internal(validLumbers, proposedLumber, thickness, highestPropertyLevel);

const getClosestLumberToProposedLumber_Internal = (
  validLumbers: readonly Lumber[],
  proposedLumber: Lumber,
  thickness: number | undefined,
  level: LumberPropertyLevel,
): Lumber => {
  const matchingLumbers = getLumbersMatchingProposalAtLevel(validLumbers, proposedLumber, thickness, level);

  if (matchingLumbers.length > 0) {
    if (level === lowestPropertyLevel) {
      // If we have gotten to this point and the species and/or grade proposed are not available, we can find the next
      // best match if possible.
      const proposedSpeciesAvailable = matchingLumbers.some((lumber) => speciesMatches(lumber, proposedLumber));
      const proposedGradeAvailable = matchingLumbers.some((lumber) => gradeMatches(lumber, proposedLumber));
      if (!proposedGradeAvailable || !proposedSpeciesAvailable) {
        let candidateLumbers = matchingLumbers;
        if (proposedSpeciesAvailable) {
          candidateLumbers = matchingLumbers.filter((lumber) => speciesMatches(lumber, proposedLumber));
        } else {
          const speciesRankings = buildRankingsIncludingComboSpecies(comboSpeciesToRank(validLumbers, proposedLumber));
          const proposedLumberIndexInSpeciesRanking = getSpeciesRank(
            proposedLumber.species,
            proposedLumber.tangibleSpecies,
            speciesRankings,
          );
          const matchingLumbersWithBetterSpecies = matchingLumbers.filter(
            (matchingLumber) =>
              getSpeciesRank(matchingLumber.species, matchingLumber.tangibleSpecies, speciesRankings) >
              proposedLumberIndexInSpeciesRanking,
          );
          const lumberWithNextBestSpecies = minBy(matchingLumbersWithBetterSpecies, (lumber) =>
            getSpeciesRank(lumber.species, lumber.tangibleSpecies, speciesRankings),
          );

          // Narrowed by what the winner is made of rather than by its species field, which reads Combo for every combo
          // and so would keep all of them however far apart their species rank. Two combos holding the same species are
          // both equally the next best species; the grade ranking below is what tells them apart.
          if (lumberWithNextBestSpecies)
            candidateLumbers = candidateLumbers.filter((lumber) => speciesMatches(lumber, lumberWithNextBestSpecies));
        }

        const gradeRankings = buildRankingsIncludingComboGrades(comboGradesToRank(validLumbers, proposedLumber));
        const proposedLumberIndexInGradeRanking = getGradeRank(
          proposedLumber.grade,
          proposedLumber.tangibleGrades,
          gradeRankings,
        );
        const matchingLumbersWithSameOrBetterGrades = candidateLumbers.filter(
          (matchingLumber) =>
            getGradeRank(matchingLumber.grade, matchingLumber.tangibleGrades, gradeRankings) >=
            proposedLumberIndexInGradeRanking,
        );
        return (
          minBy(matchingLumbersWithSameOrBetterGrades, (matchingLumber) =>
            getGradeRank(matchingLumber.grade, matchingLumber.tangibleGrades, gradeRankings),
          ) ?? candidateLumbers[0]
        );
      }
      return matchingLumbers[0];
    }
    return getClosestLumberToProposedLumber_Internal(matchingLumbers, proposedLumber, thickness, level - 1);
  }

  if (level === lowestPropertyLevel) {
    if (validLumbers.length > 0) {
      return validLumbers[0];
    }

    throw new Error("Lumber level can't be decreased further.");
  }

  return getClosestLumberToProposedLumber_Internal(validLumbers, proposedLumber, thickness, level - 1);
};

export const lumberCanBeUsedForComponentThickness = (lumber: Lumber, thickness: number) => {
  switch (lumber.structure) {
    case 'Sawn':
      return lumber.actualThickness === thickness || lumber.actualWidth === thickness;
    case 'StructuralComposite':
      return lumber.actualThickness === thickness;
    default:
      assertExhaustiveSwitch(lumber.structure, 'Unknown lumber structure.');
  }
};

export function getLumberDimension(lumber: Lumber, otherDimension: number) {
  if (lumber.actualThickness === otherDimension) return lumber.actualWidth;
  if (lumber.actualWidth === otherDimension) return lumber.actualThickness;
  throw new Error('Provided lumber dimension does not match provided lumber');
}
